

export interface SafetyCheckResult {
  processingTimeMs: number;
  modelUsed: string;
  schoolContentDetected: SchoolContentResult;
}

export interface SchoolContentResult {
  classroom: boolean;
  school: boolean;
  students: boolean;
  other: boolean;
}

class PhotoSafetyService {
  private worker: Worker | null = null;
  private workerReady: Promise<void> | null = null;

  constructor() {
    this.initializeWorker();
  }

  private initializeWorker() {
    if (typeof Worker === 'undefined') {
      console.warn('Web Workers not supported');
      return;
    }

    this.worker = new Worker(new URL('../workers/photo-safety.worker.ts', import.meta.url), {
      type: 'module'
    });

    this.worker.onmessage = (event) => {
      const { type, data } = event.data;

      if (type === 'status') {
        console.log('🔧 [PhotoSafety Service] Worker status:', data);
      }
    };

    this.workerReady = new Promise((resolve, reject) => {
      if (!this.worker) return reject('Worker not initialized');

      const handler = (event: MessageEvent) => {
        if (event.data.type === 'status' && event.data.data.status === 'ready') {
          this.worker?.removeEventListener('message', handler);
          resolve();
        }
      };

      this.worker.addEventListener('message', handler);
      this.worker.postMessage({ type: 'load' });
    });
  }

  async initialize() {
    await this.workerReady;
  }

  async checkPhotoSafety(imageBlob: Blob): Promise<SafetyCheckResult> {
    const startTime = performance.now();

    // If worker failed to initialize, return empty result immediately
    if (!this.worker) {
      console.warn('Worker not available (failed to init), returning empty result');
      return this.getEmptyResult();
    }

    try {
      // Ensure initialized (this might be a no-op if already ready, or wait if pending)
      await this.initialize();

      // Double check worker existence after await
      if (!this.worker) {
        return this.getEmptyResult();
      }

      const dataUrl = await this.blobToDataURL(imageBlob);

      return new Promise((resolve) => {
        if (!this.worker) {
          console.warn('Worker lost during check');
          return resolve(this.getEmptyResult());
        }

        const handler = (event: MessageEvent) => {
          const { type, data, error } = event.data;

          if (type === 'result') {
            this.worker?.removeEventListener('message', handler);
            const result = this.processDetectionResults(data);
            resolve({
              processingTimeMs: performance.now() - startTime,
              modelUsed: 'Xenova/owlvit-base-patch32 (Worker)',
              schoolContentDetected: result
            });
          } else if (type === 'error') {
            this.worker?.removeEventListener('message', handler);
            console.error('Worker error:', error);
            // Resolve with empty result to keep UI moving
            resolve(this.getEmptyResult());
          }
        };

        this.worker.addEventListener('message', handler);
        this.worker.postMessage({ type: 'detect', data: { image: dataUrl } });

        // Timeout after 15 seconds for detection
        setTimeout(() => {
          this.worker?.removeEventListener('message', handler);
          console.warn('Safety check timed out');
          resolve(this.getEmptyResult());
        }, 15000);
      });

    } catch (error) {
      console.error('Safety check failed:', error);
      return {
        processingTimeMs: performance.now() - startTime,
        modelUsed: 'error',
        schoolContentDetected: {
          classroom: false,
          school: false,
          students: false,
          other: false
        }
      };
    }
  }

  private processDetectionResults(objects: any[]): SchoolContentResult {
    const result: SchoolContentResult = {
      classroom: false,
      school: false,
      students: false,
      other: false
    };

    for (const obj of objects) {
      const label = obj.label?.toLowerCase() || '';
      const score = obj.score || 0;

      if (score < 0.12) continue;

      // Classroom environment
      if (label.includes('chalkboard') || label.includes('whiteboard') ||
          label.includes('blackboard') || label.includes('classroom') ||
          label.includes('projector') || label.includes('desk')) {
        result.classroom = true;
      }

      // School identifiers
      if (label.includes('badge') || label.includes('id card') ||
          label.includes('lanyard') || label.includes('name tag') ||
          label.includes('school') || label.includes('uniform') ||
          label.includes('logo') || label.includes('banner')) {
        result.school = true;
      }

      // Students
      if (label.includes('student') || label.includes('teacher')) {
        result.students = true;
      }

      // Other educational materials
      if (label.includes('book') || label.includes('notebook') ||
          label.includes('work') || label.includes('paper') ||
          label.includes('homework') || label.includes('backpack') ||
          label.includes('supplies') || label.includes('educational') ||
          label.includes('bulletin')) {
        result.other = true;
      }
    }
    return result;
  }

  private getEmptyResult(): SafetyCheckResult {
    return {
      processingTimeMs: 0,
      modelUsed: 'none',
      schoolContentDetected: {
        classroom: false,
        school: false,
        students: false,
        other: false
      }
    };
  }

  private blobToDataURL(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async preloadModels() {
    // Trigger initialization
    this.initialize().catch(console.error);
  }
}

export const photoSafetyService = new PhotoSafetyService();